import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Copyright 2022 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import * as React from 'react';
import { throttle } from 'lodash';
import { classNames } from "../../utils/helpers";
import { Flyout, useFlyoutContext } from "../_private/flyout";
import { View } from "../view";
import { Text } from "../text";
import { useResize } from "../../hooks/useResize/useResize";
import { Icon } from "../icon";
import { ChevronDown, ChevronRight } from '@az/starc-ui-icons';
import { Actionable } from "../actionable";
import { useKeyboardCallback } from "../../hooks/_private/useKeyboardCallback";
import * as keys from "../../constants/keys";
import * as DropdownKeyboard from './DropdownKeyboard';
import s from './Dropdown.module.css';
const { handleUpPress, handleDownPress, handleSpacePress, handleLeftPress, handleRightPress, handleTabPress, } = DropdownKeyboard;
export const Dropdown = ({ attributes, className, children, variant = 'tertiary', placement = 'bottom-start', padding = 0, width, open, onOpen, onClose, contentGap, closeOnScroll, }) => {
    const [elWidth, setElWidth] = React.useState();
    const rootClassName = classNames(s.root, variant === 'ghost' && s[`--variant-${variant}`], className);
    const btnRef = React.useRef(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const throttledSetElWidth = React.useCallback(throttle(newWidth => {
        setElWidth(newWidth);
    }, 40), []);
    useResize(btnRef, ({ width }) => {
        throttledSetElWidth(width);
    });
    React.useEffect(() => {
        return () => {
            throttledSetElWidth.cancel();
        };
    }, [throttledSetElWidth]);
    const handleClose = () => {
        var _a;
        onClose === null || onClose === void 0 ? void 0 : onClose();
        (_a = btnRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        closeOnScroll && (document === null || document === void 0 ? void 0 : document.removeEventListener('scroll', handleClose, true));
    };
    const handleOpen = () => {
        onOpen === null || onOpen === void 0 ? void 0 : onOpen();
        closeOnScroll && (document === null || document === void 0 ? void 0 : document.addEventListener('scroll', handleClose, true));
    };
    const getWidth = () => {
        if (width === 'trigger')
            return width;
        if (width)
            return `${width}px`;
        if (elWidth)
            return `${elWidth}px`;
        return undefined;
    };
    return (_jsx(View, { attributes: Object.assign({ 'data-testid': 'st-dropdown', ref: btnRef }, attributes), className: rootClassName, children: _jsx(Flyout, { position: placement, focusFirstTrapElement: false, triggerType: "click", contentAttributes: { style: { '--st-_p': padding } }, contentClassName: classNames(s.content, s['--has-width']), width: getWidth(), onClose: handleClose, closeOnScroll: closeOnScroll, onOpen: handleOpen, active: open, contentGap: contentGap, children: children }) }));
};
const Button = ({ children, label, icon, className, disabled, onClick }) => {
    const { flyout, handleClick } = useFlyoutContext();
    const buttonClassName = classNames(s.button, disabled && s['--disabled'], className);
    const chevronClassName = classNames(s.chevron, flyout.status === 'visible' && s['--open'], disabled && s['--disabled']);
    const handleTriggerClick = (e) => {
        e.preventDefault();
        if (disabled)
            return;
        onClick === null || onClick === void 0 ? void 0 : onClick(e);
        handleClick();
    };
    const iconElement = icon ? (_jsx(View.Item, { children: _jsx(Icon, { svg: icon, color: disabled ? 'gray-400' : 'gray-600', title: label }) })) : null;
    const generateChevronTitle = flyout.status === 'visible' ? 'Close Dropdown' : 'Open Dropdown';
    return (_jsx(Flyout.Trigger, { children: attributes => (_jsxs(View, { as: "button", direction: "row", justify: "center", align: "center", height: "var(--st-unit-13)", backgroundColor: "secondary", padding: [4, 3], gap: 0, className: buttonClassName, attributes: Object.assign(Object.assign({}, attributes), { onClick: handleTriggerClick, disabled, role: 'menu', 'aria-haspopup': 'true', 'aria-label': generateChevronTitle + ' ' + label }), children: [_jsx(View.Item, { grow: true, children: _jsxs(View, { direction: "row", align: "center", children: [_jsx(View.Item, { children: iconElement }), _jsx(View.Item, { grow: true, children: children ? (children) : (_jsx(Text, { variant: "main-body", align: "start", className: s.label, color: disabled ? 'gray-400' : 'primary', children: label })) })] }) }), _jsx(View.Item, { className: s['chevron-container'], children: _jsx(Icon, { svg: ChevronDown, size: 4, className: chevronClassName, title: generateChevronTitle }) })] })) }));
};
const Content = ({ children, className }) => {
    const contentClassName = classNames(s.content, className);
    const contentRef = React.useRef(null);
    useKeyboardCallback([keys.UP, keys.DOWN, keys.SPACE], key => {
        switch (key) {
            case keys.UP:
                return handleUpPress(contentRef);
            case keys.DOWN:
                return handleDownPress(contentRef);
            case keys.SPACE:
                return handleSpacePress(contentRef);
        }
    }, [contentRef]);
    return (_jsx(Flyout.Content, { className: contentClassName, attributes: { role: 'application' }, children: _jsx(View, { backgroundColor: "secondary", borderColor: "transparent", width: "100%", attributes: { ref: contentRef }, children: children }) }));
};
const SubMenu = ({ className, width, disabled, label, children, onOpen, onClose, }) => {
    const submenuRef = React.useRef(null);
    const triggerRef = React.useRef(null);
    useKeyboardCallback([keys.UP, keys.DOWN, keys.SPACE, keys.LEFT, keys.RIGHT], key => {
        switch (key) {
            case keys.UP:
                return handleUpPress(submenuRef);
            case keys.DOWN:
                return handleDownPress(submenuRef);
            case keys.SPACE:
                return handleSpacePress(submenuRef);
            case keys.LEFT:
                return handleLeftPress(submenuRef, triggerRef);
            case keys.RIGHT:
                return handleRightPress(submenuRef, triggerRef);
        }
    }, [submenuRef, triggerRef]);
    return (_jsxs(Flyout, { width: `${width + 8}px`, onClose: onClose, onOpen: onOpen, triggerType: "hover", returnTriggerFocus: false, position: "end", children: [_jsx(Flyout.Trigger, { children: attributes => (_jsx(View, { height: "100%", justify: "center", padding: [0, 4], className: classNames(s.item, disabled && s['--disabled']), attributes: Object.assign(Object.assign({}, attributes), { style: { opacity: '1', display: 'block' }, tabIndex: -1, 'aria-disabled': disabled, 'aria-label': 'Open dropdown submenu' }), children: _jsxs(View, { direction: "row", align: "center", attributes: { ref: triggerRef }, children: [_jsx(View.Item, { grow: true, children: _jsx(Text, { variant: "small-body", color: "gray-500", children: label }) }), _jsx(View.Item, { children: _jsx(Icon, { svg: ChevronRight, size: 4, color: disabled ? 'primary' : 'gray-400', title: "Open dropdown submenu" }) })] }) })) }), !disabled && (_jsx(Flyout.Content, { className: classNames(className, s.content, disabled && s['--disabled']), attributes: { role: 'application' }, children: _jsx(View, { attributes: { ref: submenuRef, id: 'submenu' }, children: children }) }))] }));
};
const Item = ({ className, disabled, href, label, onClick, attributes }) => {
    const itemClassName = classNames(className);
    const { handleClick, triggerElRef } = useFlyoutContext();
    const itemRef = React.useRef(null);
    React.useEffect(() => {
        document === null || document === void 0 ? void 0 : document.addEventListener('keydown', e => {
            handleTabPress(e, itemRef, triggerElRef);
        });
    }, [triggerElRef]);
    return (_jsx(Actionable, { className: itemClassName, href: disabled ? '' : href, onClick: e => {
            onClick === null || onClick === void 0 ? void 0 : onClick(e);
            handleClick();
        }, as: href ? 'a' : 'button', disabled: disabled, tabIndex: -1, attributes: Object.assign(Object.assign({}, attributes), { style: { opacity: '1', display: 'block', width: '100%' }, 'aria-label': label }), ref: itemRef, children: _jsx(View, { height: "100%", justify: "center", padding: [0, 4], className: classNames(s.item, disabled && s['--disabled']), children: _jsx(Text, { variant: "small-body", color: "gray-500", children: label }) }) }));
};
Dropdown.Button = Button;
Dropdown.Content = Content;
Dropdown.Item = Item;
Dropdown.SubMenu = SubMenu;
